import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FadeLoader } from 'react-spinners';
import {
  Card, Box, Grid, Divider, Container, Typography, Button, Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Page from '../../components/Page';
import tracker from '../../api/tracker';
import { fDateOnly } from '../../utils/formatTime';
import FuneralPrintTemplate from '../../sections/@dashboard/funerals/FuneralPrintTemplate';

const override = { margin: '0 auto' };

export default function FuneralView() {
  const { hashid } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [funeral, setFuneral] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const response = await tracker.get(`funerals/${hashid}`);
      setFuneral(response.data.data);
      setLoading(false);
    })();
  }, [hashid]);

  const f = funeral;

  return (
    <>
    <FuneralPrintTemplate funeral={f} />
    <Page title="Funeral" className="no-print">
      <Container maxWidth="xl">
        {loading || !f ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <FadeLoader cssOverride={override} color="#032C65" />
          </Box>
        ) : (
          <Card sx={{ p: 3 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
              <Typography variant="h5" fontWeight={700}>{f.deceased_name}</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/dashboard/funeral')}
                >
                  {t('Go_back')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={() => window.print()}
                  sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                >
                  {t('print')}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/dashboard/funeral/edit/${hashid}`)}
                  sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                >
                  {t('edit')}
                </Button>
              </Stack>
            </Stack>

            {/* Section cards */}
            <Grid container spacing={3}>
              {/* Deceased Information */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_deceased_info')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow label={t('name_of_deceased')} value={f.deceased_name} />
                  <InfoRow label={t('Address')} value={f.deceased_address} />
                  <InfoRow label={t('date_of_death')} value={fDateOnly(f.date_of_death, i18n.language)} />
                  <InfoRow label={t('Age')} value={f.age} />
                </Card>
              </Grid>

              {/* Family */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_family')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow label={t('next_of_kin')} value={f.next_of_kin} />
                  <InfoRow label={t('relationship')} value={f.relationship} />
                  <InfoRow label={t('Address')} value={f.family_address} />
                  <InfoRow label={t('Phone')} value={f.family_phone} />
                </Card>
              </Grid>

              {/* Funeral Home */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_funeral_home')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow label={t('funeral_home_name')} value={f.funeral_home_name} />
                  <InfoRow label={t('Address')} value={f.funeral_home_address} />
                  <InfoRow label={t('funeral_home_contact_name')} value={f.funeral_home_contact_name} />
                  <InfoRow label={t('Phone')} value={f.funeral_home_phone} />
                  <InfoRow label={t('wake_date')} value={fDateOnly(f.wake_date, i18n.language)} />
                  <InfoRow label={t('wake_time')} value={f.wake_time} />
                </Card>
              </Grid>

              {/* Funeral Mass */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_funeral_mass')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow label={t('cemetery')} value={f.cemetery} />
                  <InfoRow label={t('mass_date')} value={fDateOnly(f.mass_date, i18n.language)} />
                  <InfoRow label={t('mass_time')} value={f.mass_time} />
                  <InfoRow label={t('organist')} value={f.organist} />
                  <InfoRow label={t('organist_phone')} value={f.organist_phone} />
                  <InfoRow label={t('Language')} value={f.language} />
                </Card>
              </Grid>
            </Grid>
          </Card>
        )}
      </Container>
    </Page>
    </>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right' }}>{value || '—'}</Typography>
    </Box>
  );
}
